import { clearTokens, getAccessToken, refreshAccessToken, startLogin } from "./auth";

const API_BASE = import.meta.env.VITE_MARKETS_API_URL ?? "https://api.g1.markets";

export class ApiError extends Error {
	status: number;
	body: unknown;
	constructor(status: number, body: unknown, message: string) {
		super(message);
		this.status = status;
		this.body = body;
	}
}

export const SELECTED_ORG_KEY = "g1m.selectedOrgId";

export function getSelectedOrgId(): string | null {
	try {
		return localStorage.getItem(SELECTED_ORG_KEY);
	} catch {
		return null;
	}
}

export function setSelectedOrgId(orgId: string | null): void {
	try {
		if (orgId) localStorage.setItem(SELECTED_ORG_KEY, orgId);
		else localStorage.removeItem(SELECTED_ORG_KEY);
	} catch {
		/* localStorage may be unavailable in private mode */
	}
}

async function call<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
	const token = getAccessToken();
	const headers: Record<string, string> = {
		"content-type": "application/json",
		...(init.headers as Record<string, string> | undefined),
	};
	if (token) headers.authorization = `Bearer ${token}`;
	const orgId = getSelectedOrgId();
	if (orgId && !headers["x-org-id"]) headers["x-org-id"] = orgId;

	const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
	if (res.status === 401 && retry) {
		const ok = await refreshAccessToken();
		if (ok) return call<T>(path, init, false);
		clearTokens();
		// Don't restart the OIDC flow while AuthCallback is mid-exchange — the
		// callback page would get hijacked, aborting the code→token POST, and
		// the next /authorize would just hand back yet another code (loop).
		if (location.pathname !== "/auth/callback") {
			await startLogin(location.pathname);
		}
		throw new ApiError(401, null, "redirecting_to_login");
	}
	const text = await res.text();
	const body = text ? safeJson(text) : null;
	if (!res.ok) {
		throw new ApiError(res.status, body, `${res.status} ${init.method ?? "GET"} ${path}`);
	}
	return body as T;
}

function safeJson(t: string): unknown {
	try {
		return JSON.parse(t);
	} catch {
		return t;
	}
}

export const api = {
	get: <T>(p: string) => call<T>(p),
	post: <T>(p: string, body: unknown) => call<T>(p, { method: "POST", body: JSON.stringify(body) }),
	patch: <T>(p: string, body: unknown) =>
		call<T>(p, { method: "PATCH", body: JSON.stringify(body) }),
};

export type OfferRow = {
	id: string;
	brandOrgId: string;
	title: string;
	vertical: string;
	subvertical: string | null;
	payoutType: "cpa" | "cpl" | "revshare" | "hybrid";
	payoutAmount: number | null;
	payoutPercent: number | null;
	payoutCurrency: string;
	constraintsDsl: string;
	geos: string;
	dailyCap: number | null;
	monthlyCap: number | null;
	negotiationTtlS: number;
	status: "draft" | "active" | "paused" | "closed" | "expired";
	createdAt: number;
	updatedAt: number;
};

export type TrafficRow = {
	id: string;
	affiliateOrgId: string;
	displayName: string;
	vertical: string;
	subverticals: string;
	geos: string;
	sources: string;
	monthlyVolume: number | null;
	avgConversionRate: number | null;
	fraudScore: number | null;
	constraintsDsl: string | null;
	status: "draft" | "active" | "paused";
	createdAt: number;
	updatedAt: number;
};

export type NegotiationRow = {
	id: string;
	offerId: string;
	auctionId: string | null;
	brandOrgId: string;
	affiliateOrgId: string;
	state: string;
	ttlAt: number;
	closedReason: string | null;
	createdAt: number;
	updatedAt: number;
};

export type AgentRow = {
	id: string;
	orgId: string;
	oidcClientId: string;
	displayName: string;
	status: "active" | "paused" | "disabled";
	pausedReason: string | null;
	pausedAt: number | null;
	createdAt: number;
};

export type ProvisionAgentResponse = {
	agent: {
		id: string;
		orgId: string;
		oidcClientId: string;
		displayName: string;
		role: "brand" | "affiliate";
		status: "active";
	};
	oidcClientSecret: string | null;
	notice: string;
};

export type OrgMembership = {
	id: string;
	kind: "brand" | "affiliate";
	legalName: string;
	displayName: string;
	role: string;
};

export type MeResponse = {
	user: { id: string; email: string | null };
	org: OrgMembership | null;
	orgs?: OrgMembership[];
};
